import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ICategory, IUser } from '../../../types/damTypes';
import {
  addAiImageComment,
  approveAiImageOutput,
  deleteAiImageOutput,
  deleteAiImageGeneration,
  fetchAiImageArchiveOutputs,
  fetchAiImageDamAsset,
  fetchAiImageGeneration,
  fetchAiImageRecipeAsset,
  fetchAiImageCategories,
  fetchAiImageGenerations,
  fetchAiImagePresets,
  fetchAiImageStudioConfig,
  fetchCommentedOutputCount,
  generateAiImages,
  lookupAiImageProductReference,
  markAiImageCommentsRead,
  publishAiImageOutput,
  rejectAiImageOutput,
  replaceRecipeAssetWithAiImageOutput,
  reuseAiImageOutput,
  searchAiImageDamAssets,
  searchAiImageRecipeAssets,
} from '../aiImageStudioApi';
import { refreshAiCommentBadge } from './useAiCommentBadge';
import { getSelectableStudioModels, resolveSelectableModelId } from '../modelCatalog';
import {
  AiImageGeneration,
  AiImageArchiveOutputItem,
  AiImageGenerateErrorResponse,
  AiImageHistoryFilters,
  AiImageMutationResult,
  AiImagePendingGeneration,
  AiImagePublishSelection,
  AiImagePreset,
  AiImageReferenceInput,
  AiImageStudioConfig,
  AiImageStudioDraft,
  DamSearchAssetResult,
  ProductReferenceLookupResult,
  RecipeSearchAssetResult,
} from '../types';
import { AiImageGalleryScope } from '../galleryScope';

const DRAFT_STORAGE_KEY = 'ai-image-studio-draft-v1';
const RECENT_HISTORY_LIMIT = 25;
const ARCHIVE_OUTPUT_PAGE_SIZE = 100;
const UPSCALE_PRESET_SLUG = 'recipe-upscale-high-resolution';
const UPSCALE_FALLBACK_PROMPT = `Upscale the image to high resolution (4K or higher) and perform professional color correction.

The image may contain food, product packaging, people, environments, graphic design, or other non-food content.

GOAL:
Create a high-quality, professional version of the same image with improved resolution, better color, and more realistic detail.

APPLY:

1. Resolution & detail:
- Increase resolution significantly
- Improve sharpness and micro-detail
- Add realistic texture ONLY to food (e.g. crispy surfaces, moisture, fibers, grains, oil sheen)

2. Color & tonal correction:
- Fix exposure (recover highlights, lift shadows)
- Correct white balance (neutral whites, no color cast)
- Improve contrast and depth
- Increase natural color richness (avoid washed-out look)
- Improve tonal coverage (better separation in shadows/midtones/highlights)

3. Food enhancement (allowed):
- If the image contains food: enhance texture and realism in food only
- If the image does not contain food: preserve the subject exactly and improve only resolution, sharpness, color balance, noise, and tonal quality
- Make food look more appetizing and natural when food is present
- Add subtle imperfections for realism only where appropriate (not perfect or artificial)

STRICT RULES:

- Product packaging, labels, logos, and text MUST remain 100% unchanged
- Do NOT alter colors, layout, typography, or shapes on packaging
- Do NOT add or remove any objects
- Do NOT change composition, crop, or camera angle

- Background and environment should remain consistent
- No stylization, no cinematic grading, no AI-art look

AVOID:
- Over-sharpening
- HDR look
- Over-saturation
- Artificial or fake-looking textures

RESULT:
The final image should look like the same photo, but shot with a high-end camera under perfect lighting - with improved color and higher resolution. If food is present, enhance food texture. If no food is present, do not invent food or change the subject. Packaging remains pixel-perfect identical.`;

function makeReferenceId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getModelConfig(config: AiImageStudioConfig | null | undefined, modelId?: string) {
  if (!modelId) return null;
  return getSelectableStudioModels(config).find((model) => model.id === modelId)
    || config?.models.find((model) => model.id === modelId)
    || null;
}

function getModelOutputFormats(config: AiImageStudioConfig | null | undefined, modelId?: string): string[] {
  const modelConfig = getModelConfig(config, modelId);
  if (modelConfig?.outputFormats?.length) {
    return modelConfig.outputFormats;
  }
  if (modelConfig?.provider === 'openai') {
    return config?.openAiOutputFormats?.length ? config.openAiOutputFormats : ['jpeg', 'png', 'webp'];
  }
  return ['jpeg'];
}

function normalizeOutputFormat(outputFormats: string[], preferred?: string): string {
  if (preferred && outputFormats.includes(preferred)) {
    return preferred;
  }
  if (outputFormats.includes('jpeg')) {
    return 'jpeg';
  }
  return outputFormats[0] || 'jpeg';
}

export function normalizeDraftForConfig(draft: AiImageStudioDraft, config?: AiImageStudioConfig | null): AiImageStudioDraft {
  if (!config) return draft;
  const selectableModelId = resolveSelectableModelId(draft.model, config);
  const modelConfig = getModelConfig(config, selectableModelId);
  if (modelConfig) {
    return {
      ...draft,
      model: selectableModelId,
      provider: modelConfig.provider,
      outputFormat: normalizeOutputFormat(getModelOutputFormats(config, selectableModelId), draft.outputFormat),
    };
  }

  const fallbackModel = getModelConfig(config, config.defaultModel) || config.models[0] || null;
  if (!fallbackModel) return draft;
  return {
    ...draft,
    provider: fallbackModel.provider,
    model: fallbackModel.id,
    outputFormat: normalizeOutputFormat(getModelOutputFormats(config, fallbackModel.id), draft.outputFormat),
  };
}

function createDefaultDraft(config?: AiImageStudioConfig | null): AiImageStudioDraft {
  const defaultModel = getModelConfig(config, config?.defaultModel) || config?.models?.[0] || null;
  const defaultOutputFormats = getModelOutputFormats(config, defaultModel?.id || config?.defaultModel);
  return {
    provider: defaultModel?.provider || config?.provider || 'gemini',
    model: defaultModel?.id || config?.defaultModel || '',
    prompt: '',
    presetId: '',
    workflow: 'standard',
    promptTemplateId: '',
    templatePrompt: '',
    manualPromptEdited: false,
    targetAssetId: '',
    aspectRatio: config?.aspectRatios?.includes('1:1') ? '1:1' : (config?.aspectRatios?.[0] || '1:1'),
    imageSize: config?.imageSizes?.includes('1K') ? '1K' : (config?.imageSizes?.[0] || '1K'),
    quality: config?.openAiQualities?.includes('auto') ? 'auto' : (config?.openAiQualities?.[0] || 'auto'),
    outputFormat: normalizeOutputFormat(defaultOutputFormats, 'jpeg'),
    count: config?.countRange?.min || 1,
    references: [],
  };
}

function restoreDraft(config?: AiImageStudioConfig | null): AiImageStudioDraft {
  const defaults = createDefaultDraft(config);
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    return normalizeDraftForConfig({
      ...defaults,
      ...parsed,
      references: Array.isArray(parsed?.references)
        ? parsed.references.map((reference: any) => ({
            ...reference,
            referenceId: typeof reference?.referenceId === 'string' && reference.referenceId
              ? reference.referenceId
              : makeReferenceId(reference?.type || 'ref'),
          }))
        : [],
    }, config);
  } catch {
    return defaults;
  }
}

function toPersistableDraft(draft: AiImageStudioDraft): AiImageStudioDraft {
  return {
    ...draft,
    editSource: draft.editSource ? { ...draft.editSource, dataUrl: undefined } : undefined,
    editMask: undefined,
    references: draft.references
      .filter((reference) => reference.type !== 'upload')
      .map((reference) => ({ ...reference, dataUrl: undefined })),
  };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`Kunde inte läsa filen ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

function mapGenerationReferencesToDraft(generation: AiImageGeneration): AiImageReferenceInput[] {
  return generation.references.map((reference) => ({
    referenceId: reference.referenceId || makeReferenceId(reference.type),
    type: reference.type,
    assetId: reference.assetId,
    generationId: reference.generationId,
    outputId: reference.outputId,
    sku: reference.sku,
    url: reference.sourceUrl,
    thumbnailUrl: reference.thumbnailUrl,
    mimeType: reference.mimeType,
    fileName: reference.fileName,
    title: reference.title,
  }));
}

function getMutationGenerationFromError(error: any): AiImageGeneration | null {
  return error?.response?.data?.generation || null;
}

function getAiImageErrorMessage(error: any): string {
  const responseData = error?.response?.data as AiImageGenerateErrorResponse | undefined;
  return responseData?.error?.userMessage
    || responseData?.message
    || error?.message
    || 'Kunde inte generera bilder.';
}

function createPendingGeneration(draft: AiImageStudioDraft): AiImagePendingGeneration {
  return {
    requestId: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    prompt: draft.prompt,
    model: draft.model,
    aspectRatio: draft.aspectRatio,
    imageSize: draft.imageSize,
    count: draft.count,
    createdAt: new Date().toISOString(),
    workflow: draft.workflow || 'standard',
    editMode: draft.editMode,
  };
}

function sameGalleryScope(left: AiImageGalleryScope | null, right: AiImageGalleryScope | null): boolean {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return left.workflow === right.workflow && left.editMode === right.editMode;
}

export function useAiImageStudioSession(currentUser: IUser | null) {
  const [config, setConfig] = useState<AiImageStudioConfig | null>(null);
  const [presets, setPresets] = useState<AiImagePreset[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [history, setHistory] = useState<AiImageGeneration[]>([]);
  const [galleryGenerations, setGalleryGenerations] = useState<AiImageGeneration[]>([]);
  const galleryScopeRef = useRef<AiImageGalleryScope | null>(null);
  const galleryRequestRef = useRef(0);
  const [historyFilters, setHistoryFilters] = useState<AiImageHistoryFilters>({
    search: '',
    status: '',
    model: '',
    archiveCategory: '',
  });
  const [archiveOutputs, setArchiveOutputs] = useState<AiImageArchiveOutputItem[]>([]);
  const [archiveCursor, setArchiveCursor] = useState<string | null>(null);
  const [archiveHasMore, setArchiveHasMore] = useState(true);
  const [archiveLoaded, setArchiveLoaded] = useState(false);
  const [draft, setDraft] = useState<AiImageStudioDraft>(() => restoreDraft(null));
  const [currentGeneration, setCurrentGeneration] = useState<AiImageGeneration | null>(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveLoadingMore, setArchiveLoadingMore] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pendingGenerations, setPendingGenerations] = useState<AiImagePendingGeneration[]>([]);
  const generationQueueRef = useRef<Array<{ pending: AiImagePendingGeneration; request: any }>>([]);
  const processingRef = useRef(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [commentedCount, setCommentedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const notify = useCallback((
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info' = 'info',
  ) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const reloadPresets = useCallback(async () => {
    const loadedPresets = await fetchAiImagePresets();
    setPresets(loadedPresets);
    setDraft((prev) => {
      if (!prev.presetId || loadedPresets.some((preset) => preset._id === prev.presetId)) {
        return prev;
      }
      return {
        ...prev,
        presetId: '',
        promptTemplateId: prev.promptTemplateId === prev.presetId ? '' : prev.promptTemplateId,
      };
    });
    return loadedPresets;
  }, []);

  const refreshGallery = useCallback(async (scope?: AiImageGalleryScope | null, clear = false) => {
    const nextScope = scope === undefined ? galleryScopeRef.current : scope;
    if (scope !== undefined) {
      galleryScopeRef.current = nextScope;
    }
    const requestId = ++galleryRequestRef.current;
    if (!nextScope) {
      setGalleryGenerations([]);
      return;
    }
    if (clear) {
      setGalleryGenerations([]);
    }
    try {
      const generations = await fetchAiImageGenerations({
        limit: RECENT_HISTORY_LIMIT,
        ...(nextScope.workflow ? { workflow: nextScope.workflow } : {}),
        ...(nextScope.editMode ? { editMode: nextScope.editMode } : {}),
      });
      if (requestId !== galleryRequestRef.current) return;
      setGalleryGenerations(generations);
    } catch (loadError: any) {
      if (requestId !== galleryRequestRef.current) return;
      setSnackbar({
        open: true,
        message: loadError?.response?.data?.message || loadError?.message || 'Kunde inte hämta bilder för den här vyn.',
        severity: 'error',
      });
    }
  }, []);

  const setGalleryScope = useCallback((scope: AiImageGalleryScope | null) => {
    if (sameGalleryScope(galleryScopeRef.current, scope)) return;
    void refreshGallery(scope, true);
  }, [refreshGallery]);

  const loadHistory = useCallback(async (filters?: Partial<AiImageHistoryFilters>) => {
    setHistoryLoading(true);
    try {
      const generations = await fetchAiImageGenerations({
        search: filters?.search || undefined,
        status: filters?.status || undefined,
        model: filters?.model || undefined,
        limit: RECENT_HISTORY_LIMIT,
      });
      setHistory(generations);
      await refreshGallery();
    } catch (loadError: any) {
      setSnackbar({
        open: true,
        message: loadError?.response?.data?.message || loadError?.message || 'Kunde inte hämta historik.',
        severity: 'error',
      });
    } finally {
      setHistoryLoading(false);
    }
  }, [refreshGallery]);

  const loadArchiveOutputs = useCallback(async (
    filters?: Partial<AiImageHistoryFilters>,
    append = false
  ) => {
    const nextFilters = {
      ...historyFilters,
      ...filters,
    };
    const cursor = append ? archiveCursor : null;

    if (append && (!archiveHasMore || archiveLoadingMore || archiveLoading)) {
      return;
    }

    if (append) {
      setArchiveLoadingMore(true);
    } else {
      setArchiveLoading(true);
    }

    try {
      const page = await fetchAiImageArchiveOutputs({
        search: nextFilters.search || undefined,
        status: nextFilters.status || undefined,
        model: nextFilters.model || undefined,
        archiveCategory: nextFilters.archiveCategory || undefined,
        cursor: cursor || undefined,
        limit: ARCHIVE_OUTPUT_PAGE_SIZE,
      });

      setArchiveOutputs((prev) => (append ? [...prev, ...page.items] : page.items));
      setArchiveCursor(page.nextCursor);
      setArchiveHasMore(page.hasMore);
      setArchiveLoaded(true);
    } catch (loadError: any) {
      setSnackbar({
        open: true,
        message: loadError?.response?.data?.message || loadError?.message || 'Kunde inte hämta AI-arkivet.',
        severity: 'error',
      });
    } finally {
      if (append) {
        setArchiveLoadingMore(false);
      } else {
        setArchiveLoading(false);
      }
    }
  }, [archiveCursor, archiveHasMore, archiveLoading, archiveLoadingMore, historyFilters]);

  useEffect(() => {
    let isMounted = true;

    const loadInitial = async () => {
      setLoading(true);
      setError(null);
      try {
        const [loadedConfig, loadedPresets, loadedCategories, loadedHistory, loadedCommentCount] = await Promise.all([
          fetchAiImageStudioConfig(),
          fetchAiImagePresets(),
          fetchAiImageCategories(),
          fetchAiImageGenerations({ limit: RECENT_HISTORY_LIMIT }),
          fetchCommentedOutputCount().catch(() => 0),
        ]);

        if (!isMounted) return;

        setConfig(loadedConfig);
        setPresets(loadedPresets);
        setCategories(loadedCategories);
        setHistory(loadedHistory);
        setCommentedCount(loadedCommentCount as number);

        setDraft((prev) => {
          const restored = restoreDraft(loadedConfig);
          const shouldReplaceWithDefaults = !prev.model && !prev.prompt && prev.references.length === 0;
          return shouldReplaceWithDefaults ? restored : normalizeDraftForConfig({
            ...prev,
            provider: prev.provider || loadedConfig.provider,
            model: prev.model || loadedConfig.defaultModel,
            aspectRatio: prev.aspectRatio || (loadedConfig.aspectRatios[0] || '1:1'),
            imageSize: prev.imageSize || (loadedConfig.imageSizes[0] || '1K'),
          }, loadedConfig);
        });
      } catch (loadError: any) {
        if (!isMounted) return;
        setError(loadError?.response?.data?.message || loadError?.message || 'Kunde inte ladda Athena AI Studio.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (currentUser) {
      loadInitial();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  useEffect(() => {
    if (!config) return;
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(toPersistableDraft(draft)));
  }, [config, draft]);

  const setDraftField = useCallback(<K extends keyof AiImageStudioDraft>(key: K, value: AiImageStudioDraft[K]) => {
    setDraft((prev) => {
      if (key === 'model' && typeof value === 'string') {
        const selectableModelId = resolveSelectableModelId(value, config);
        const modelConfig = getModelConfig(config, selectableModelId);
        if (modelConfig) {
          return {
            ...prev,
            model: selectableModelId,
            provider: modelConfig.provider,
            outputFormat: normalizeOutputFormat(getModelOutputFormats(config, selectableModelId), prev.outputFormat),
          };
        }
      }
      return { ...prev, [key]: value };
    });
  }, [config]);

  const replaceDraftFromGeneration = useCallback((generation: AiImageGeneration) => {
    setDraft((prev) => normalizeDraftForConfig({
      ...prev,
      provider: generation.provider,
      model: generation.model,
      prompt: generation.prompt,
      presetId: generation.presetId || '',
      workflow: generation.workflow || 'standard',
      promptTemplateId: generation.promptTemplateId || '',
      templatePrompt: generation.templatePrompt || '',
      manualPromptEdited: Boolean(generation.manualPromptEdited),
      targetAssetId: generation.targetAssetId || '',
      aspectRatio: generation.aspectRatio,
      imageSize: generation.imageSize,
      quality: prev.quality || 'auto',
      outputFormat: prev.outputFormat,
      count: generation.outputCount,
      references: mapGenerationReferencesToDraft(generation),
    }, config));
  }, [config]);

  const applyPreset = useCallback((presetId: string) => {
    const preset = presets.find((item) => item._id === presetId);
    if (!preset) {
      setDraft((prev) => ({ ...prev, presetId: '' }));
      return;
    }

    setDraft((prev) => {
      const basePrompt = prev.prompt.trim();
      const presetPrompt = preset.promptTemplate.trim();
      let nextPrompt = basePrompt;

      if (preset.appendMode === 'replace') {
        nextPrompt = presetPrompt;
      } else if (preset.appendMode === 'prepend') {
        nextPrompt = [presetPrompt, basePrompt].filter(Boolean).join('\n\n');
      } else {
        nextPrompt = [basePrompt, presetPrompt].filter(Boolean).join('\n\n');
      }

      return {
        ...prev,
        prompt: nextPrompt,
        presetId,
      };
    });
  }, [presets]);

  const addReferences = useCallback((incomingReferences: AiImageReferenceInput[]) => {
    setDraft((prev) => {
      const maxReferences = config?.maxReferences || 14;
      const remaining = Math.max(maxReferences - prev.references.length, 0);
      const sliced = incomingReferences.slice(0, remaining);
      return {
        ...prev,
        references: [...prev.references, ...sliced],
      };
    });
  }, [config]);

  const addUploadedFiles = useCallback(async (files: FileList | File[]) => {
    const fileList = Array.from(files);
    const dataReferences = await Promise.all(
      fileList.map(async (file) => ({
        referenceId: makeReferenceId('upload'),
        type: 'upload' as const,
        fileName: file.name,
        mimeType: file.type || 'image/jpeg',
        title: file.name,
        dataUrl: await readFileAsDataUrl(file),
      }))
    );

    addReferences(dataReferences);
  }, [addReferences]);

  const addDamAssetReference = useCallback((asset: DamSearchAssetResult) => {
    addReferences([
      {
        referenceId: makeReferenceId('dam'),
        type: 'dam_asset',
        assetId: asset._id,
        url: asset.previewUrl,
        thumbnailUrl: asset.thumbnailUrl,
        title: asset.title,
        fileName: asset.fileName,
        mimeType: asset.fileType,
      },
    ]);
  }, [addReferences]);

  const addSkuProductReference = useCallback((asset: DamSearchAssetResult, sku: string) => {
    addReferences([
      {
        referenceId: makeReferenceId('sku'),
        type: 'sku_product',
        assetId: asset._id,
        sku,
        url: asset.previewUrl,
        thumbnailUrl: asset.thumbnailUrl,
        title: asset.title,
        fileName: asset.fileName,
        mimeType: asset.fileType,
      },
    ]);
  }, [addReferences]);

  const addGeneratedReference = useCallback(async (generationId: string, outputId: string) => {
    const reference = await reuseAiImageOutput(generationId, outputId);
    addReferences([
      {
        referenceId: reference.referenceId || makeReferenceId('generated'),
        type: 'generated',
        generationId: reference.generationId,
        outputId: reference.outputId,
        url: reference.url,
        thumbnailUrl: reference.thumbnailUrl,
        title: reference.title,
        fileName: reference.fileName,
        mimeType: reference.mimeType,
      },
    ]);
  }, [addReferences]);

  const removeReference = useCallback((referenceId: string) => {
    setDraft((prev) => ({
      ...prev,
      references: prev.references.filter((reference) => reference.referenceId !== referenceId),
    }));
  }, []);

  const searchDamReferences = useCallback(async (query: string, categoryId?: string) => {
    return searchAiImageDamAssets(query, categoryId);
  }, []);

  const searchRecipeReferences = useCallback(async (query: string): Promise<RecipeSearchAssetResult[]> => {
    return searchAiImageRecipeAssets(query);
  }, []);

  const fetchRecipeReference = useCallback(async (assetId: string): Promise<RecipeSearchAssetResult> => {
    return fetchAiImageRecipeAsset(assetId);
  }, []);

  const fetchDamReference = useCallback(async (assetId: string): Promise<DamSearchAssetResult> => {
    return fetchAiImageDamAsset(assetId);
  }, []);

  const lookupProductReference = useCallback(async (sku: string): Promise<ProductReferenceLookupResult> => {
    return lookupAiImageProductReference(sku);
  }, []);

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    while (generationQueueRef.current.length > 0) {
      const item = generationQueueRef.current[0];
      setGenerating(true);

      try {
        const generation = await generateAiImages(item.request);
        setCurrentGeneration(generation);
        await loadHistory();
        if (archiveLoaded) {
          await loadArchiveOutputs(undefined, false);
        }
        if (generation.status === 'failed') {
          setSnackbar({
            open: true,
            message: generation.errorMessage || 'Genereringen misslyckades.',
            severity: 'error',
          });
        } else if (generation.errorMessage) {
          setSnackbar({
            open: true,
            message: generation.errorMessage,
            severity: 'warning',
          });
        } else {
          setSnackbar({ open: true, message: 'Bilder genererade.', severity: 'success' });
        }
      } catch (generationError: any) {
        setSnackbar({
          open: true,
          message: getAiImageErrorMessage(generationError),
          severity: 'error',
        });
      }

      generationQueueRef.current.shift();
      setPendingGenerations((prev) => prev.filter((p) => p.requestId !== item.pending.requestId));
    }

    setGenerating(false);
    processingRef.current = false;
  }, [archiveLoaded, loadArchiveOutputs, loadHistory]);

  const generateFromDraft = useCallback((sourceDraft: AiImageStudioDraft) => {
    if (!sourceDraft.prompt.trim()) {
      setSnackbar({ open: true, message: 'Skriv en prompt först.', severity: 'warning' });
      return;
    }

    const requestModel = resolveSelectableModelId(sourceDraft.model || config?.defaultModel, config)
      || config?.defaultModel
      || 'gemini-3.1-flash-image';
    const requestModelConfig = getModelConfig(config, requestModel);
    const requestProvider = requestModelConfig?.provider || sourceDraft.provider || config?.provider || 'gemini';
    const outputFormat = normalizeOutputFormat(getModelOutputFormats(config, requestModel), sourceDraft.outputFormat);
    const pending = createPendingGeneration({
      ...sourceDraft,
      provider: requestProvider,
      model: requestModel,
      outputFormat,
    });
    const request = {
      provider: requestProvider,
      model: requestModel,
      prompt: sourceDraft.prompt,
      presetId: sourceDraft.presetId || undefined,
      workflow: sourceDraft.workflow || 'standard',
      promptTemplateId: sourceDraft.promptTemplateId || undefined,
      templatePrompt: sourceDraft.templatePrompt || undefined,
      manualPromptEdited: Boolean(sourceDraft.manualPromptEdited),
      targetAssetId: sourceDraft.targetAssetId || undefined,
      aspectRatio: sourceDraft.aspectRatio,
      imageSize: sourceDraft.imageSize,
      quality: sourceDraft.quality || 'auto',
      outputFormat,
      count: sourceDraft.count,
      editMode: sourceDraft.editMode,
      editSource: sourceDraft.editSource,
      editMask: sourceDraft.editMask,
      preserveOriginalOutsideMask: sourceDraft.preserveOriginalOutsideMask,
      references: sourceDraft.references,
    };

    generationQueueRef.current.push({ pending, request });
    setPendingGenerations((prev) => [...prev, pending]);
    void processQueue();
  }, [config, processQueue]);

  const generate = useCallback(() => {
    generateFromDraft(draft);
  }, [draft, generateFromDraft]);

  const upscaleOutput = useCallback(async (generationId: string, outputId: string) => {
    try {
      const reference = await reuseAiImageOutput(generationId, outputId);
      const upscalePreset = presets.find((preset) => preset.slug === UPSCALE_PRESET_SLUG) || null;
      const requestModel = config?.defaultModel || 'gemini-3.1-flash-image';
      const requestModelConfig = getModelConfig(config, requestModel);
      const prompt = upscalePreset?.promptTemplate || UPSCALE_FALLBACK_PROMPT;

      generateFromDraft({
        provider: requestModelConfig?.provider || config?.provider || 'gemini',
        model: requestModel,
        prompt,
        presetId: upscalePreset?._id || '',
        workflow: 'standard',
        promptTemplateId: upscalePreset?._id,
        templatePrompt: prompt,
        manualPromptEdited: false,
        targetAssetId: undefined,
        aspectRatio: 'Auto',
        imageSize: '4K',
        quality: 'auto',
        outputFormat: 'jpeg',
        count: 1,
        references: [
          {
            referenceId: reference.referenceId || makeReferenceId('generated'),
            type: 'generated',
            generationId: reference.generationId,
            outputId: reference.outputId,
            url: reference.url,
            thumbnailUrl: reference.thumbnailUrl,
            title: reference.title || 'Bild att skala upp',
            fileName: reference.fileName,
            mimeType: reference.mimeType,
          },
        ],
      });
      setSnackbar({ open: true, message: 'Skalning startad.', severity: 'info' });
    } catch (upscaleError: any) {
      setSnackbar({
        open: true,
        message: upscaleError?.response?.data?.message || upscaleError?.message || 'Kunde inte starta skalning.',
        severity: 'error',
      });
    }
  }, [config, generateFromDraft, presets]);

  const openGeneration = useCallback(async (generationId: string) => {
    try {
      const generation = await fetchAiImageGeneration(generationId);
      setCurrentGeneration(generation);
      replaceDraftFromGeneration(generation);
    } catch (openError: any) {
      setSnackbar({
        open: true,
        message: openError?.response?.data?.message || openError?.message || 'Kunde inte öppna generationen.',
        severity: 'error',
      });
    }
  }, [replaceDraftFromGeneration]);

  const loadGenerationDetails = useCallback(async (generationId: string): Promise<AiImageGeneration | null> => {
    try {
      const generation = await fetchAiImageGeneration(generationId);
      setCurrentGeneration(generation);
      return generation;
    } catch (openError: any) {
      setSnackbar({
        open: true,
        message: openError?.response?.data?.message || openError?.message || 'Kunde inte öppna generationen.',
        severity: 'error',
      });
      return null;
    }
  }, []);

  const syncArchiveGeneration = useCallback((generation: AiImageGeneration) => {
    setArchiveOutputs((prev) => prev
      .map((item) => {
        if (item.generationId !== generation._id) {
          return item;
        }
        const output = generation.outputs.find((candidate) => candidate._id === item.outputId);
        if (!output) {
          return null;
        }
        return {
          ...item,
          provider: generation.provider,
          model: generation.model,
          prompt: generation.prompt,
          promptResolved: generation.promptResolved,
          presetId: generation.presetId,
          presetName: generation.presetName,
          promptTemplateId: generation.promptTemplateId,
          templatePrompt: generation.templatePrompt,
          manualPromptEdited: generation.manualPromptEdited,
          workflow: generation.workflow,
          targetAssetId: generation.targetAssetId,
          aspectRatio: generation.aspectRatio,
          imageSize: generation.imageSize,
          outputCount: generation.outputCount,
          generationStatus: generation.status,
          userName: generation.userName,
          createdAt: generation.createdAt,
          updatedAt: generation.updatedAt,
          references: generation.references,
          output,
        };
      })
      .filter((item): item is AiImageArchiveOutputItem => Boolean(item)));
  }, []);

  const handleMutationResult = useCallback(async (
    promise: Promise<AiImageMutationResult | AiImageGeneration>,
    actionKey: string,
    successMessage: string
  ) => {
    setActionLoading(actionKey);
    try {
      const result = await promise;
      const generation = 'generation' in result ? result.generation : result;
      setCurrentGeneration(generation);
      syncArchiveGeneration(generation);
      await loadHistory();
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
    } catch (mutationError: any) {
      const generation = getMutationGenerationFromError(mutationError);
      if (generation) {
        setCurrentGeneration(generation);
        syncArchiveGeneration(generation);
        await loadHistory();
      }
      setSnackbar({
        open: true,
        message: mutationError?.response?.data?.message || mutationError?.message || 'Åtgärden misslyckades.',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  }, [loadHistory, syncArchiveGeneration]);

  const approveOutput = useCallback(async (
    generationId: string,
    outputId: string,
    selection: AiImagePublishSelection
  ) => {
    await handleMutationResult(
      approveAiImageOutput(generationId, outputId, selection),
      `approve:${outputId}`,
      'Bilden godkändes och publicerades till DAM.'
    );
  }, [handleMutationResult]);

  const rejectOutput = useCallback(async (generationId: string, outputId: string, reason?: string) => {
    await handleMutationResult(
      rejectAiImageOutput(generationId, outputId, reason),
      `reject:${outputId}`,
      'Bilden markerades som avslagen.'
    );
  }, [handleMutationResult]);

  const publishOutput = useCallback(async (
    generationId: string,
    outputId: string,
    selection: AiImagePublishSelection
  ) => {
    await handleMutationResult(
      publishAiImageOutput(generationId, outputId, selection),
      `publish:${outputId}`,
      'Bilden publicerades till DAM.'
    );
  }, [handleMutationResult]);

  const replaceRecipeAsset = useCallback(async (
    generationId: string,
    outputId: string,
    targetAssetId: string,
    note?: string
  ) => {
    await handleMutationResult(
      replaceRecipeAssetWithAiImageOutput(generationId, outputId, targetAssetId, note),
      `replace-recipe:${outputId}`,
      'Receptbilden ersattes och den gamla bilden sparades som version.'
    );
  }, [handleMutationResult]);

  const syncStoredGeneration = useCallback((updated: AiImageGeneration) => {
    setHistory((prev) => prev.map((generation) => (generation._id === updated._id ? updated : generation)));
    setGalleryGenerations((prev) => prev.map((generation) => (generation._id === updated._id ? updated : generation)));
    setCurrentGeneration((prev) => (prev?._id === updated._id ? updated : prev));
  }, []);

  const addComment = useCallback(async (generationId: string, outputId: string, text: string) => {
    setActionLoading(`comment:${outputId}`);
    try {
      const updated = await addAiImageComment(generationId, outputId, text);
      syncStoredGeneration(updated);
      syncArchiveGeneration(updated);
      setSnackbar({ open: true, message: 'Kommentaren sparades.', severity: 'success' });
      fetchCommentedOutputCount().then((c) => setCommentedCount(c)).catch(() => {});
      refreshAiCommentBadge();
    } catch (commentError: any) {
      setSnackbar({
        open: true,
        message: commentError?.response?.data?.message || commentError?.message || 'Kunde inte spara kommentaren.',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  }, [syncArchiveGeneration, syncStoredGeneration]);

  const markRead = useCallback(async (generationId: string, outputId: string) => {
    try {
      const updated = await markAiImageCommentsRead(generationId, outputId);
      syncStoredGeneration(updated);
      syncArchiveGeneration(updated);
      refreshAiCommentBadge();
    } catch {
      // silent
    }
  }, [syncArchiveGeneration, syncStoredGeneration]);

  const deleteGeneration = useCallback(async (generationId: string) => {
    setActionLoading(`delete:${generationId}`);
    try {
      await deleteAiImageGeneration(generationId);
      setHistory((prev) => prev.filter((generation) => generation._id !== generationId));
      setGalleryGenerations((prev) => prev.filter((generation) => generation._id !== generationId));
      setArchiveOutputs((prev) => prev.filter((item) => item.generationId !== generationId));
      setCurrentGeneration((prev) => (prev?._id === generationId ? null : prev));
      setSnackbar({ open: true, message: 'Generationen raderades.', severity: 'success' });
    } catch (deleteError: any) {
      setSnackbar({
        open: true,
        message: deleteError?.response?.data?.message || deleteError?.message || 'Kunde inte radera generationen.',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  }, []);

  const deleteOutput = useCallback(async (generationId: string, outputId: string) => {
    setActionLoading(`delete-output:${outputId}`);
    try {
      const updated = await deleteAiImageOutput(generationId, outputId);
      syncStoredGeneration(updated);
      syncArchiveGeneration(updated);
      setDraft((prev) => ({
        ...prev,
        references: prev.references.filter((reference) => !(
          reference.type === 'generated'
          && reference.generationId === generationId
          && reference.outputId === outputId
        )),
      }));
      fetchCommentedOutputCount().then((c) => setCommentedCount(c)).catch(() => {});
      refreshAiCommentBadge();
      setSnackbar({ open: true, message: 'Bilden raderades.', severity: 'success' });
    } catch (deleteError: any) {
      setSnackbar({
        open: true,
        message: deleteError?.response?.data?.message || deleteError?.message || 'Kunde inte radera bilden.',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  }, [syncArchiveGeneration, syncStoredGeneration]);

  const reuseAll = useCallback((generation: AiImageGeneration) => {
    replaceDraftFromGeneration(generation);
  }, [replaceDraftFromGeneration]);

  const referenceCountLabel = useMemo(() => {
    const max = config?.maxReferences || 14;
    return `${draft.references.length}/${max}`;
  }, [config?.maxReferences, draft.references.length]);

  return {
    config,
    presets,
    categories,
    history,
    galleryGenerations,
    setGalleryScope,
    archiveOutputs,
    historyFilters,
    setHistoryFilters,
    draft,
    setDraft,
    setDraftField,
    currentGeneration,
    loading,
    historyLoading,
    archiveLoading,
    archiveLoadingMore,
    archiveHasMore,
    archiveLoaded,
    generating,
    pendingGenerations,
    actionLoading,
    error,
    snackbar,
    closeSnackbar,
    notify,
    referenceCountLabel,
    loadHistory,
    reloadPresets,
    loadArchiveOutputs,
    generate,
    generateFromDraft,
    openGeneration,
    loadGenerationDetails,
    applyPreset,
    addUploadedFiles,
    addDamAssetReference,
    addSkuProductReference,
    addGeneratedReference,
    upscaleOutput,
    removeReference,
    searchDamReferences,
    searchRecipeReferences,
    fetchDamReference,
    fetchRecipeReference,
    lookupProductReference,
    approveOutput,
    rejectOutput,
    publishOutput,
    replaceRecipeAsset,
    reuseAll,
    addComment,
    markRead,
    deleteOutput,
    deleteGeneration,
    commentedCount,
  };
}
